import type { Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";

/**
 * Strong dropdown surface used across management pages: deep shadow +
 * hairline border so open menus never blend into the page background.
 * Same recipe as the Client & User Setup autocomplete dropdowns.
 * Typed as a concrete theme function (not SxProps) so it composes inside
 * sx arrays: `sx={[STRONG_DROPDOWN_PAPER_SX, { width }]}`.
 */
export const STRONG_DROPDOWN_PAPER_SX = (
  theme: Theme,
): SystemStyleObject<Theme> => ({
  boxShadow: theme.shadows[12],
  border: "1px solid",
  borderColor: "divider",
});

/** For `Select` / `TextField select` menus: `MenuProps={strongSelectMenuProps}`. */
export const strongSelectMenuProps = {
  PaperProps: { elevation: 0, sx: STRONG_DROPDOWN_PAPER_SX },
} as const;

/** For `Autocomplete`: `componentsProps={strongAutocompletePaperProps}`.
 * Also restyles the options to match Select menus (inset rounded highlight
 * from the MuiMenuItem theme override — Autocomplete `li`s are not
 * MenuItems, so the theme does not reach them). */
export const strongAutocompletePaperProps = {
  paper: {
    elevation: 0,
    sx: [
      STRONG_DROPDOWN_PAPER_SX,
      {
        "& .MuiAutocomplete-option": {
          borderRadius: 1,
          mx: "6px",
          my: "2px",
          fontSize: 14,
        },
      },
    ],
  },
} as const;
